import { z } from "zod";
import { MEMBER_DUE_STATUS } from "./collections.js";

/* ===========================================================================
 * US-E10.F1-0075 — Debts / Offset / Loan / Pay-on-behalf (@UC0061/0062/0054)
 * Phase 7 — @gate money,ledger 🔴.
 *
 * B1: mọi số tiền = STRING minor unit (BigInt-as-string, KHÔNG float).
 * ========================================================================= */

const MONEY_INTEGER_STRING_PATTERN = /^\d+$/;

/* ---------------------------------------------------------------------------
 * Pay-on-behalf (US-1 · @UC0061/0062)
 * ------------------------------------------------------------------------- */

export const PAY_ON_BEHALF_STATUS = {
  PROPOSED: "proposed",
  ACCEPTED: "accepted",
  DECLINED: "declined",
  /** POB-PROPOSAL-EXPIRE (QĐ PO 06/10): suất đã KHÉP (paid · paid_on_behalf · waived) khi đề nghị còn treo — máy chuyển trong CÙNG tx khép suất. */
  EXPIRED: "expired",
} as const;

export type PayOnBehalfStatus =
  (typeof PAY_ON_BEHALF_STATUS)[keyof typeof PAY_ON_BEHALF_STATUS];

/** POST /v1/member-dues/:id/pay-on-behalf — A đề xuất nộp hộ due của B (AC1). */
export const proposePayOnBehalfResponseSchema = z
  .object({
    payOnBehalfId: z.string().uuid(),
    memberDueId: z.string().uuid(),
    payerMemberId: z.string().uuid(),
    beneficiaryMemberId: z.string().uuid(),
    status: z.literal(PAY_ON_BEHALF_STATUS.PROPOSED),
    proposedAt: z.string().datetime(),
  })
  .strict();

export type ProposePayOnBehalfResponse = z.infer<
  typeof proposePayOnBehalfResponseSchema
>;

export const PAY_ON_BEHALF_DECISION = {
  ACCEPT: "accept",
  DECLINE: "decline",
} as const;

export const respondPayOnBehalfRequestSchema = z
  .object({
    decision: z.enum([PAY_ON_BEHALF_DECISION.ACCEPT, PAY_ON_BEHALF_DECISION.DECLINE]),
  })
  .strict();

export type RespondPayOnBehalfRequest = z.infer<
  typeof respondPayOnBehalfRequestSchema
>;

/** POST /v1/pay-on-behalf/:id/respond — B accept/decline (AC2/AC3). */
export const respondPayOnBehalfResponseSchema = z
  .object({
    payOnBehalfId: z.string().uuid(),
    memberDueId: z.string().uuid(),
    status: z.enum([PAY_ON_BEHALF_STATUS.ACCEPTED, PAY_ON_BEHALF_STATUS.DECLINED]),
    /** Null khi decline (không ledger); có khi accept. */
    ledgerTxId: z.string().uuid().nullable(),
    newDueStatus: z.enum([
      MEMBER_DUE_STATUS.PAID_ON_BEHALF,
      MEMBER_DUE_STATUS.PENDING,
    ]),
    respondedAt: z.string().datetime(),
  })
  .strict();

export type RespondPayOnBehalfResponse = z.infer<
  typeof respondPayOnBehalfResponseSchema
>;

/**
 * 🔴 PAY-ON-BEHALF-409-CODES (@UC0061 @UC0062) — MÃ ỔN ĐỊNH cho 409 của cửa nộp hộ. Trước đây `PayOnBehalfService` ném
 * `ConflictException(<chuỗi trần>)` ⇒ problem+json `code: "CONFLICT"` chung — client (và chat AI `codeOf`) không phân biệt được với
 * quỹ đóng băng / nhóm tạm ngưng / mọi 409 khác (CLAUDE.md §7: `code` là nguồn hiển thị). `detail` giữ câu cũ (client cũ đọc `detail`).
 *
 * Khoản đã khép (`paid` · `paid_on_behalf`) dùng lại `MEMBER_DUE_NOT_OPEN` (+ `params.status`); đua khoá idempotency dùng lại
 * `MEMBER_DUE_CONCURRENT_WRITE` — cùng sự thật với cửa ghi nhận / duyệt, cùng mã. Ba mã dưới là sự thật RIÊNG của lời đề nghị nộp hộ.
 */
/** Đề nghị nộp hộ cho CHÍNH suất mình (người trả = người được trả). Không `params`. Lối đúng: tự báo đã chuyển. */
export const PAY_ON_BEHALF_SELF_CODE = "PAY_ON_BEHALF_SELF" as const;
/**
 * 🔴 FIX-1272 S-L6 (FUND-DEBT-REPAY-PATH · QĐ PO 06/10 "ba cửa thu") — khoản "nợ với quỹ" (đợt hệ thống `fund_debt`) KHÔNG nhận nộp hộ:
 * nộp hộ biến nợ QUỸ thành nợ NGƯỜI↔NGƯỜI (cửa thứ tư PO chưa chốt). Chặn ở đề nghị VÀ ở đồng ý (đề nghị cũ). Không `params`. Lối đúng:
 * người nợ tự nộp / thủ quỹ ghi nhận.
 */
export const PAY_ON_BEHALF_FUND_DEBT_CODE = "PAY_ON_BEHALF_FUND_DEBT" as const;
/** Khoản đã có MỘT lời đề nghị nộp hộ đang chờ người kia trả lời (chống hai lời chấp nhận đua nhau). Không `params`. */
export const PAY_ON_BEHALF_ALREADY_PROPOSED_CODE = "PAY_ON_BEHALF_ALREADY_PROPOSED" as const;
/**
 * Lời đề nghị KHÔNG còn chờ trả lời (đã chấp nhận / đã từ chối). `params.status` = trạng thái HIỆN TẠI của lời đề nghị (đọc dưới khoá
 * dòng, `PAY_ON_BEHALF_STATUS`). Lời đề nghị không còn tồn tại ⇒ 404, không phải mã này.
 */
export const PAY_ON_BEHALF_NOT_PENDING_CODE = "PAY_ON_BEHALF_NOT_PENDING" as const;
/**
 * POB-PROPOSAL-EXPIRE (QĐ PO 06/10 (3)) — 422: khoản SỐ-MỞ (`amountMinor = 0`, chưa có nghĩa vụ cụ thể) không đề nghị nộp hộ được — không có
 * con số nào để nộp thay (trước: đề nghị lọt, thẻ chat "0 đ", chấp nhận 409 sai nghĩa). Không `params`.
 */
export const PAY_ON_BEHALF_OPEN_AMOUNT_CODE = "PAY_ON_BEHALF_OPEN_AMOUNT" as const;

/**
 * 🔴 DEBTS-409-CODES (@UC0054 @UC0061 @UC9312, review #1184) — MÃ ỔN ĐỊNH cho các 409 còn lại của module nợ/vay. Trước đây ném
 * `ConflictException(<chuỗi trần>)` ⇒ problem+json `code: "CONFLICT"` chung — client không phân biệt được lý do (CLAUDE.md §7: `code` là
 * nguồn hiển thị). `detail` giữ NGUYÊN câu cũ (flutter-web `ApiException` đọc `detail`). Không đổi HTTP status, không đổi hành vi tiền.
 * Cấn trừ là cửa của MỘT khoản ⇒ dùng lại `MEMBER_DUE_NOT_OPEN` / `MEMBER_DUE_CONCURRENT_WRITE` (cùng sự thật với ghi nhận / nộp hộ).
 */
/** Khoản mượn đã hoàn trả đủ (`status = repaid`) — không còn gì để trả. Không `params`. */
export const LOAN_ALREADY_REPAID_CODE = "LOAN_ALREADY_REPAID" as const;
/**
 * Bút toán cho mượn / hoàn trả đụng khoá idempotency mà một yêu cầu KHÁC vừa ghi (P2002 `unique(fundId, idempotencyKey)`). 0 bút toán của
 * lượt này; tải lại sẽ thấy kết quả của yêu cầu kia. Không `params`.
 */
export const LOAN_CONCURRENT_WRITE_CODE = "LOAN_CONCURRENT_WRITE" as const;
/** Cấn trừ mà thành viên không có số dư thừa (advance_credit khả dụng = 0). Không `params`. */
export const OFFSET_NO_CREDIT_CODE = "OFFSET_NO_CREDIT" as const;
/**
 * Cấn trừ vào khoản thu SỐ MỞ chưa có số nghĩa vụ (`amountMinor = 0` — đợt `open_amount`, người đóng tự khai số lúc ghi nhận): không có "phần
 * còn lại" để cấn. 422. Không `params`. (Trước OFFSET-PARTIAL-PAIDAMOUNT: 500 — `computeOffset` ném lỗi trần.)
 */
export const OFFSET_OPEN_AMOUNT_DUE_CODE = "OFFSET_OPEN_AMOUNT_DUE" as const;
/** Tự tất toán nợ với chính mình (người nợ = người được nợ). Không `params`. */
export const PEER_SETTLEMENT_SELF_CODE = "PEER_SETTLEMENT_SELF" as const;
/** Không còn nợ ròng với người kia để tất toán (đọc dưới khoá, nguồn sổ nợ append-only). Không `params`. */
export const PEER_SETTLEMENT_NOTHING_OWED_CODE = "PEER_SETTLEMENT_NOTHING_OWED" as const;
/**
 * Yêu cầu tất toán KHÔNG còn mở (đã `settled` / `rejected`). `params.status` = trạng thái HIỆN TẠI (đọc dưới khoá dòng,
 * `PEER_SETTLEMENT_STATUS`). Yêu cầu không tồn tại ⇒ 404, không phải mã này.
 */
export const PEER_SETTLEMENT_NOT_OPEN_CODE = "PEER_SETTLEMENT_NOT_OPEN" as const;
/** Bút toán tất toán đụng khoá idempotency mà yêu cầu KHÁC vừa ghi (đua chấp nhận). 0 bút toán của lượt này. Không `params`. */
export const PEER_SETTLEMENT_CONCURRENT_WRITE_CODE = "PEER_SETTLEMENT_CONCURRENT_WRITE" as const;
/**
 * 409 hoàn tiền dây chuyền, 0 bút toán của lượt này, không `params`. HAI nghĩa: (1) bút toán đụng khoá idempotency mà yêu cầu KHÁC vừa ghi;
 * (2) CASCADE-REFUND-HARDEN — khoản chi vừa có thêm người gánh giữa lúc lấy tập người nợ và lúc khoá (đọc lại dưới khoá) ⇒ thử lại CÙNG khoá.
 */
export const CASCADE_REFUND_CONCURRENT_WRITE_CODE = "CASCADE_REFUND_CONCURRENT_WRITE" as const;
/**
 * 🔴 CASCADE-REFUND-HARDEN (@UC9220 @UC9222) — mã ổn định cho các lỗi còn lại của hoàn tiền dây chuyền (trước: chuỗi trần ⇒ `code` chung).
 * `detail` giữ câu cũ. Không đổi HTTP status.
 */
/** 422 — số hoàn ≤ 0. Không `params`. */
export const CASCADE_REFUND_AMOUNT_INVALID_CODE = "CASCADE_REFUND_AMOUNT_INVALID" as const;
/** 422 — khoản chi không còn ai gánh nợ ròng > 0 (chưa sinh nợ chia, hoặc đã tất toán/hoàn hết). Không `params`. */
export const CASCADE_REFUND_NO_BURDEN_CODE = "CASCADE_REFUND_NO_BURDEN" as const;
/**
 * 422 — số hoàn vượt Σ nợ ròng CÒN LẠI (đọc DƯỚI khoá vị thế). `params.remainingMinor` = Σ còn hoàn được (chuỗi minor) để client nói "còn hoàn
 * được tối đa X".
 */
export const CASCADE_REFUND_EXCEEDS_BURDEN_CODE = "CASCADE_REFUND_EXCEEDS_BURDEN" as const;
/** 404 — khoản chi không thuộc quỹ này (không tồn tại · quỹ khác · id rác) — một câu trả lời cho mọi trường hợp (chống dò). Không `params`. */
export const CASCADE_REFUND_EXPENSE_NOT_FOUND_CODE = "CASCADE_REFUND_EXPENSE_NOT_FOUND" as const;
/**
 * 🔴 CASCADE-REFUND-SETTLED-DEBT (@UC9220) — 422: phần hoàn chia cho một người nợ VƯỢT nợ ròng HIỆN TẠI của họ với người ứng (theo CẶP người, mọi
 * khoản — đã tất toán thật / được tha / đã được hoàn trước đó). Ghi tiếp sẽ đẩy nợ ròng của họ thành ÂM (người ứng nợ ngược). Chặn fail-closed;
 * phần NCC hoàn tương ứng người đã trả xong thuộc về ai là câu PO riêng. `params`: `memberId` (người nợ đầu tiên vượt, theo thứ tự memberId),
 * `allocatedMinor` (phần hoàn chia cho họ), `netMinor` (nợ ròng hiện tại) — chuỗi minor.
 */
export const CASCADE_REFUND_EXCEEDS_DEBTOR_NET_CODE = "CASCADE_REFUND_EXCEEDS_DEBTOR_NET" as const;
/** Xin ẩn danh dữ liệu ở nhóm mà người gọi VẪN là thành viên — phải rời nhóm trước. Không `params`. */
export const ANONYMIZE_STILL_MEMBER_CODE = "ANONYMIZE_STILL_MEMBER" as const;

/* ---------------------------------------------------------------------------
 * Offset / cấn trừ (US-2 · @UC0054)
 * ------------------------------------------------------------------------- */

/** POST /v1/member-dues/:id/offset — cấn advance_credit ↔ due unpaid (treasurer). */
export const offsetResponseSchema = z
  .object({
    memberDueId: z.string().uuid(),
    offsetAmountMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    remainingDueMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    currency: z.string().length(3),
    fullyOffset: z.boolean(),
    /** OFFSET ledger tx (nội bộ MEMBER_PAYABLE↔MEMBER_RECEIVABLE) — luôn có. */
    ledgerTxId: z.string().uuid(),
    // OFFSET-PARTIAL-PAIDAMOUNT (PR-b): cấn một phần ⇒ `partially_paid` (luỹ kế ghi nhận, cùng khuôn record-paid) — cấn luôn > 0 nên
    // không còn kết cục `pending`.
    newDueStatus: z.enum([MEMBER_DUE_STATUS.PAID, MEMBER_DUE_STATUS.PARTIALLY_PAID]),
    offsetAt: z.string().datetime(),
  })
  .strict();

export type OffsetResponse = z.infer<typeof offsetResponseSchema>;

/* ---------------------------------------------------------------------------
 * Loan / mượn tạm (US-3 · @UC0054)
 * ------------------------------------------------------------------------- */

export const LOAN_STATUS = {
  OPEN: "open",
  PARTIALLY_REPAID: "partially_repaid",
  REPAID: "repaid",
} as const;

export type LoanStatus = (typeof LOAN_STATUS)[keyof typeof LOAN_STATUS];

export const createLoanRequestSchema = z
  .object({
    fundId: z.string().uuid(),
    borrower: z.string().min(1).max(200),
    principalMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    currency: z.string().length(3),
  })
  .strict();

export type CreateLoanRequest = z.infer<typeof createLoanRequestSchema>;

export const loanResponseSchema = z
  .object({
    loanId: z.string().uuid(),
    fundId: z.string().uuid(),
    borrower: z.string(),
    principalMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    outstandingMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    currency: z.string().length(3),
    status: z.enum([LOAN_STATUS.OPEN, LOAN_STATUS.PARTIALLY_REPAID, LOAN_STATUS.REPAID]),
    ledgerTxId: z.string().uuid(),
    createdAt: z.string().datetime(),
  })
  .strict();

export type LoanResponse = z.infer<typeof loanResponseSchema>;

export const loanRepaymentRequestSchema = z
  .object({
    amountMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
  })
  .strict();

export type LoanRepaymentRequest = z.infer<typeof loanRepaymentRequestSchema>;

export const loanRepaymentResponseSchema = z
  .object({
    loanId: z.string().uuid(),
    repaymentMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    outstandingMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    status: z.enum([LOAN_STATUS.OPEN, LOAN_STATUS.PARTIALLY_REPAID, LOAN_STATUS.REPAID]),
    ledgerTxId: z.string().uuid(),
    repaidAt: z.string().datetime(),
  })
  .strict();

export type LoanRepaymentResponse = z.infer<typeof loanRepaymentResponseSchema>;

/* ---------------------------------------------------------------------------
 * Settlement collection (US-5 · @UC0053)
 * POST /v1/funds/:id/collections kind=settlement — tất toán cuối kỳ.
 * ------------------------------------------------------------------------- */

/** Optional title (không gửi → service dùng mặc định). Không nhận amount (tính từ vị thế ledger). */
export const createSettlementRequestSchema = z
  .object({
    title: z.string().trim().min(1).max(120).optional(),
  })
  .strict();

export type CreateSettlementRequest = z.infer<typeof createSettlementRequestSchema>;

export const settlementResponseSchema = z
  .object({
    collectionId: z.string().uuid(),
    fundId: z.string().uuid(),
    kind: z.literal("settlement"),
    title: z.string(),
    currency: z.string().length(3),
    /** Số member_dues sinh (chỉ member còn nợ; member dư/không nợ KHÔNG sinh — AC4). */
    duesCreated: z.number().int().nonnegative(),
    createdAt: z.string().datetime(),
  })
  .strict();

export type SettlementResponse = z.infer<typeof settlementResponseSchema>;

/* ---------------------------------------------------------------------------
 * Peer-debt settlement (US-E15.F1-0176 · @UC9211/9212/9213)
 * Trả nợ member↔member (on_behalf/expense_split). claim (debtor) → respond (creditor).
 * Tiền = STRING minor-unit (BigInt ở service). .strict() từ chối field lạ.
 * ------------------------------------------------------------------------- */

export const PEER_SETTLEMENT_STATUS = {
  CLAIMED: "claimed",
  SETTLED: "settled",
  REJECTED: "rejected",
  /**
   * US-E15.F5-0180 (@UC9223) — trạng thái TRUNG THỰC THỨ BA: "đã chuyển – chưa đối chiếu". Con nợ
   * đã báo chuyển (claimed) và (peer nhỏ) hết X giờ → worker chuyển sang đây — KHÔNG phải "đã nhận"
   * (settled). Máy KHÔNG mạo danh lời người (P2.2): trạng thái này KHÔNG đóng nợ, KHÔNG post ledger,
   * KHÔNG đổi số dư quỹ thật. Creditor vẫn bấm "đã nhận" (→ settled) hoặc "chưa nhận" (→ pending/claimed).
   */
  TRANSFERRED_UNRECONCILED: "transferred_unreconciled",
} as const;

export type PeerSettlementStatus =
  (typeof PEER_SETTLEMENT_STATUS)[keyof typeof PEER_SETTLEMENT_STATUS];

export const PEER_SETTLEMENT_DECISION = {
  ACCEPT: "accept",
  DECLINE: "decline",
} as const;

export type PeerSettlementDecision =
  (typeof PEER_SETTLEMENT_DECISION)[keyof typeof PEER_SETTLEMENT_DECISION];

/** POST /v1/funds/:fundId/debts/settle — debtor CLAIM "đã chuyển" (AC1). Lump: refDebtEntryIds ≥1. */
export const claimPeerSettlementSchema = z
  .object({
    debtorMemberId: z.string().uuid().optional(),
    creditorMemberId: z.string().uuid(),
    refDebtEntryIds: z.array(z.string().uuid()).min(1),
    amountMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
  })
  .strict();

export type ClaimPeerSettlementRequest = z.infer<typeof claimPeerSettlementSchema>;

/** POST /v1/funds/:fundId/debts/settlements/:id/respond — creditor accept/decline. */
export const respondPeerSettlementSchema = z
  .object({
    decision: z.enum([
      PEER_SETTLEMENT_DECISION.ACCEPT,
      PEER_SETTLEMENT_DECISION.DECLINE,
    ]),
    idempotencyKey: z.string().min(1).max(200),
  })
  .strict();

export type RespondPeerSettlementRequest = z.infer<
  typeof respondPeerSettlementSchema
>;

export const peerSettlementResponseSchema = z
  .object({
    settlementId: z.string().uuid(),
    status: z.enum([
      PEER_SETTLEMENT_STATUS.CLAIMED,
      PEER_SETTLEMENT_STATUS.SETTLED,
      PEER_SETTLEMENT_STATUS.REJECTED,
      PEER_SETTLEMENT_STATUS.TRANSFERRED_UNRECONCILED,
    ]),
    /**
     * US-E15.F5-0180 (@UC9225) — nhánh xác nhận theo nguồn/ngưỡng (chỉ có ở claim peer). Client hiển
     * thị "chờ 72h" / "chờ chủ nợ" / "bắt tay". Optional (respond không cần).
     */
    mode: z
      .enum([
        "manual_only",
        "auto_after_72h",
        "hold_until_creditor",
        "require_handshake",
      ])
      .optional(),
    /** Số đã trả (bút toán settle vừa post; 0 khi claim/reject). */
    settledMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    /** Nợ ròng CÒN LẠI debtor→creditor sau bước này (≥0). */
    remainingNetMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    currency: z.string().length(3),
    /** Null khi claim/reject (chưa/không post); có khi accept post SETTLEMENT. */
    ledgerTxId: z.string().uuid().nullable(),
    /** Null khi mới claim (chưa creditor phản hồi). */
    respondedAt: z.string().datetime().nullable(),
  })
  .strict();

export type PeerSettlementResponse = z.infer<typeof peerSettlementResponseSchema>;

/* ---------------------------------------------------------------------------
 * Financial position guard codes (US-4)
 * ------------------------------------------------------------------------- */

export const FINANCIAL_POSITION_CODE = {
  NONZERO: "FINANCIAL_POSITION_NONZERO",
} as const;

export const FINANCIAL_POSITION_NONZERO_MESSAGE =
  "Không thể thực hiện: thành viên còn vị thế tài chính chưa tất toán (khoản nợ/mượn/phải đóng).";

/**
 * US-E19.F8-0858 (@UC0004) — LÝ DO máy-đọc vì sao `DELETE /v1/me` bị chặn, đi trong `params.reason`
 * của problem+json (§7 CLAUDE.md — `code` là mã ỔN ĐỊNH, chi tiết máy-đọc nằm ở `params`).
 *
 * 🔴 VÌ SAO KHÔNG tách thành hai `code` MỚI: app đã phát hành lên store chỉ biết
 * `FINANCIAL_POSITION_NONZERO` (`delete_my_account_usecase.dart`). Đổi `code` ⇒ bản cũ rơi vào nhánh
 * "lỗi lạ" và MẤT màn giải thích đang có — một regress lặng cho người dùng chưa kịp cập nhật. Giữ
 * `code` + thêm `params.reason` cho ra **cả hai**: bản cũ hiển thị y như hôm nay, bản mới nêu đúng MỘT
 * lý do. Đây cũng đúng chỗ mà error model đã dành sẵn, không phải lách.
 */
export const DELETE_ME_BLOCKED_REASON = {
  /** Còn là chủ nhóm ĐANG SỐNG có quỹ hoạt động ⇒ việc cần làm: chuyển quyền chủ nhóm. */
  OWNER_ACTIVE_FUND: "owner_active_fund",
  /** Còn vị thế tài chính chưa tất toán ⇒ việc cần làm: tất toán. */
  FINANCIAL_POSITION: "financial_position",
} as const;

export type DeleteMeBlockedReason =
  (typeof DELETE_ME_BLOCKED_REASON)[keyof typeof DELETE_ME_BLOCKED_REASON];

/**
 * Câu cho từng lý do — P5.3: **0 thuật ngữ kế toán**, và nói VIỆC CẦN LÀM chứ không mô tả trạng thái
 * hệ thống. Câu gộp cũ (`OWNER_HAS_FUNDS_MESSAGE`) đã gỡ: nó nêu hai lý do bằng chữ "hoặc" nên người
 * đọc phải tự đoán mình thuộc ca nào, rồi đi làm một việc không liên quan và quay lại vẫn 409.
 */
export const DELETE_ME_BLOCKED_MESSAGE: Readonly<Record<DeleteMeBlockedReason, string>> = {
  [DELETE_ME_BLOCKED_REASON.OWNER_ACTIVE_FUND]:
    "Không thể xoá tài khoản: bạn đang là chủ của nhóm còn hoạt động. Hãy chuyển quyền chủ nhóm cho người khác, rồi thử lại.",
  // 🔴 TRUNG TÍNH VỀ CHIỀU — cố ý. `hasNonZeroPosition` chặn cả khi **nhóm nợ người dùng**
  // (`advance_credit - offset > 0`: member ứng dư). Câu khẳng định "bạn còn nợ / hãy trả" sẽ bảo
  // đúng một chủ nợ đi trả tiền. Câu cũ (`OWNER_HAS_FUNDS_MESSAGE`) cũng sai chỗ này; đừng "sửa cho
  // rõ" bằng cách thêm chiều vào — muốn nói chiều thì phải đọc `FinancialPositionService` trước.
  // Cũng KHÔNG dùng "tất toán": từ này bị cấm trên UI (QĐ PO 27/09, `accounting_vocabulary_l10n_test`).
  [DELETE_ME_BLOCKED_REASON.FINANCIAL_POSITION]:
    "Không thể xoá tài khoản: bạn còn khoản tiền chưa dứt điểm với nhóm. Hãy hoàn tất, rồi thử lại.",
} as const;

/** DELETE /v1/me — soft-delete + anonymize (AC3). */
export const deleteMeResponseSchema = z
  .object({
    status: z.literal("anonymized"),
    deletedAt: z.string().datetime(),
  })
  .strict();

export type DeleteMeResponse = z.infer<typeof deleteMeResponseSchema>;

/* ---------------------------------------------------------------------------
 * Advance-credit / ứng trước — quỹ nợ member (US-E15.F3-0178 · @UC9217/9218/9219)
 * PRODUCER duy nhất còn thiếu để OffsetService chạy (offset.service.ts:199 chờ
 * DebtEntry kind='advance_credit'). Member nộp dư / treasurer cấp credit → quỹ ghi
 * nợ với member. Tiền = STRING minor-unit (BigInt ở service). .strict() từ chối field lạ.
 * ------------------------------------------------------------------------- */

/** POST /v1/funds/:fundId/advance-credit — treasurer ghi member ứng trước (quỹ nợ member). */
export const grantAdvanceCreditSchema = z
  .object({
    /** groupMember.id của member được ghi credit (validate ở service: ACTIVE cùng group fund). */
    memberId: z.string().uuid(),
    /** Số tiền ứng (minor-unit, string số nguyên > 0). "0"/"1.5" → reject. */
    amountMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN).refine((v) => BigInt(v) > 0n, {
      message: "amountMinor phải > 0",
    }),
    /** Bắt buộc — scope idempotency (không double-post cùng key). */
    idempotencyKey: z.string().trim().min(1).max(200),
    reason: z.string().trim().max(500).optional(),
  })
  .strict();

export type GrantAdvanceCreditRequest = z.infer<typeof grantAdvanceCreditSchema>;

export const advanceCreditResponseSchema = z
  .object({
    debtEntryId: z.string().uuid(),
    /** ADVANCE_CREDIT ledger tx (DEBIT CASH / CREDIT MEMBER_PAYABLE) — luôn có. */
    ledgerTxId: z.string().uuid(),
    memberId: z.string().uuid(),
    /** credit khả dụng SAU grant = Σ advance_credit − Σ offset (string minor-unit). */
    availableCreditMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    currency: z.string().length(3),
    createdAt: z.string().datetime(),
  })
  .strict();

export type AdvanceCreditResponse = z.infer<typeof advanceCreditResponseSchema>;
